import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchHistoricalPrices } from "../api/artistQuoteService";

const localeFor = (language) => {
  if (language?.startsWith("de")) return "de-DE";
  if (language?.startsWith("en")) return "en-GB";
  return "fr-FR";
};

export default function TrendAlerts({ artistName, medium }) {
  const { t, i18n } = useTranslation();
  const [alert, setAlert] = useState(null);
  const locale = localeFor(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    async function analyzeTrend() {
      if (!artistName || !medium) {
        setAlert(null);
        return;
      }

      const data = await fetchHistoricalPrices(artistName, medium);
      if (!data || data.length < 2) {
        setAlert(null);
        return;
      }

      const previousMonths = data.slice(-7, -1);
      const current = data[data.length - 1];
      if (!previousMonths.length) {
        setAlert(null);
        return;
      }

      const pastAverage = previousMonths.reduce(
        (sum, item) => sum + Number.parseFloat(item.average_value || 0),
        0,
      ) / previousMonths.length;

      if (!pastAverage) {
        setAlert(null);
        return;
      }

      const variation = ((Number(current.average_value) - pastAverage) / pastAverage) * 100;

      if (variation > 15) setAlert({ type: "positive", variation });
      else if (variation < -15) setAlert({ type: "negative", variation });
      else setAlert(null);
    }
    analyzeTrend();
  }, [artistName, medium]);

  if (!alert) return null;

  const formattedVariation = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(alert.variation));

  return (
    <div
      className={`mt-4 p-4 rounded border-l-4 ${
        alert.type === "positive"
          ? "bg-green-50 border-green-500 text-green-800"
          : "bg-red-50 border-red-500 text-red-800"
      }`}
    >
      <p className="text-sm font-medium">
        {t(`artistQuoteTools.trendAlerts.${alert.type}`, { value: formattedVariation })}
      </p>
    </div>
  );
}
